import { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { format, parseISO } from 'date-fns';

interface EditSubmittedDateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (newDate: string) => Promise<void>;
    currentDate: string | null;
    saving?: boolean;
}

const EditSubmittedDateModal: React.FC<EditSubmittedDateModalProps> = ({
    isOpen,
    onClose,
    onSave,
    currentDate,
    saving = false,
}) => {
    const [selectedDate, setSelectedDate] = useState<string>('');

    useEffect(() => {
        if (isOpen && currentDate) {
            // Format the date for the input (YYYY-MM-DD)
            try {
                const parsed = parseISO(currentDate);
                setSelectedDate(format(parsed, 'yyyy-MM-dd'));
            } catch {
                setSelectedDate('');
            }
        } else if (isOpen) {
            setSelectedDate('');
        }
    }, [isOpen, currentDate]);

    if (!isOpen) {
        return null;
    }

    const handleSave = async () => {
        if (!selectedDate) return;
        await onSave(selectedDate);
    };

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
            <div className='relative w-full max-w-md rounded-lg bg-white shadow-lg'>
                <button
                    className='text-gray-600 hover:text-gray-900 absolute right-4 top-4'
                    onClick={onClose}
                    disabled={saving}
                >
                    <RxCross2 />
                </button>
                <div className='p-8'>
                    <h2 className='mb-6 text-[22px] font-bold'>
                        Edit Submitted Date
                    </h2>

                    <div className='mb-6'>
                        <label
                            htmlFor='submitted-date'
                            className='mb-2 block text-sm font-medium text-gray-700'
                        >
                            Submitted Date
                        </label>
                        <input
                            type='date'
                            id='submitted-date'
                            className='w-full rounded-md border border-gray-300 p-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            disabled={saving}
                        />
                        {currentDate && (
                            <p className='mt-2 text-sm text-gray-500'>
                                Current: {format(parseISO(currentDate), 'MM/dd/yyyy')}
                            </p>
                        )}
                    </div>

                    <div className='flex justify-end space-x-4'>
                        <button
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                            onClick={onClose}
                            disabled={saving}
                        >
                            CANCEL
                        </button>
                        <button
                            className={`rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 ${
                                selectedDate && !saving
                                    ? 'border-warning bg-warning text-black hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                    : 'cursor-not-allowed border-gray-300 bg-gray-300 text-gray-700 opacity-50'
                            }`}
                            onClick={handleSave}
                            disabled={!selectedDate || saving}
                        >
                            {saving ? 'SAVING...' : 'SAVE'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditSubmittedDateModal;

